/**
 * Phase 197-H — Dynamic AI Performance Blueprint Card
 *
 * Premium cinematic dark card surfacing the AI-generated home training
 * blueprint for a single student. Pulls from
 * `GET /api/students/{id}/ai-recommendations` and renders the cached
 * `ai_insights_v197` payload.
 *
 * Visibility per Phase 197-H spec (3c): students + parents + academy_owner
 * + coaches/employees + super_admin all see the same card.
 *
 * States:
 *   • loading           → soft skeleton
 *   • no_card           → nothing renders (parent doesn't need to see "wait
 *                          for coach" empty-state on every page)
 *   • pending           → "AI is composing…" with a manual retry pulse
 *   • ready             → full blueprint card
 *   • error             → silent (logged); the page stays clean
 *
 * Staff (academy_owner / coach / super_admin) see an extra "Regenerate"
 * pill that drops the cache and re-calls Gemini.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, Flame, Dumbbell, Calendar, RefreshCw, Loader2, Heart, Video,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STAFF_ROLES = new Set(['academy_owner', 'coach', 'super_admin']);

export default function StudentAIRecommendationCard({ studentId, compact = false }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, data: null });
  const [regenerating, setRegenerating] = useState(false);

  const canRegenerate = STAFF_ROLES.has(user?.role);

  const fetchBlueprint = useCallback(async (refresh = false) => {
    if (!studentId) return;
    if (refresh) setRegenerating(true);
    else setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(
        `/students/${encodeURIComponent(studentId)}/ai-recommendations`,
        { params: refresh ? { refresh: true } : {} },
      );
      setState({ loading: false, data: r.data });
    } catch (_e) {
      setState({ loading: false, data: null });
    } finally {
      setRegenerating(false);
    }
  }, [studentId]);

  useEffect(() => { fetchBlueprint(false); }, [fetchBlueprint]);

  // Quietly hide if no card exists (no noise on parent dashboards)
  if (state.loading) {
    return (
      <div
        className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 animate-pulse"
        data-testid="ai-blueprint-skeleton"
      >
        <div className="h-3 w-32 bg-slate-800 rounded mb-3"></div>
        <div className="h-5 w-48 bg-slate-800 rounded mb-4"></div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-slate-800/70 rounded"></div>
          <div className="h-3 w-5/6 bg-slate-800/70 rounded"></div>
        </div>
      </div>
    );
  }

  const { data } = state;
  if (!data || data.status === 'no_card') return null;

  if (data.status === 'pending') {
    return (
      <div
        className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 border border-slate-800/80 rounded-2xl p-5 text-white shadow-xl relative overflow-hidden"
        data-testid="ai-blueprint-pending"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20 text-emerald-400">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-black tracking-tight">AI Coach's Blueprint</h4>
            <p className="text-[10px] text-slate-500">Personalising your next training cycle…</p>
          </div>
          <button
            onClick={() => fetchBlueprint(false)}
            className="p-1.5 text-slate-400 hover:text-emerald-400 rounded transition"
            data-testid="ai-blueprint-retry"
            title="Check again"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
          AI is composing — refresh in a few seconds.
        </div>
      </div>
    );
  }

  if (data.status !== 'ready' || !data.blueprint) return null;

  const bp = data.blueprint;
  const ctx = bp.score_context || {};
  const isLLM = bp.source === 'llm';

  return (
    <div
      className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 border border-slate-800/80 rounded-2xl p-5 sm:p-6 text-white shadow-xl shadow-emerald-500/[0.02] relative overflow-hidden font-sans"
      data-testid="ai-blueprint-card"
    >
      {/* Decorative bloom */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/[0.06] rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 relative z-10">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20 text-emerald-400">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-black tracking-tight" data-testid="ai-blueprint-headline">
              AI Coach's Training Blueprint
            </h4>
            <p className="text-[10px] text-slate-500">
              {isLLM ? 'Personalised to this card · ' : 'Heuristic anchor · '}
              auto-refreshes when a new progress card is logged
            </p>
          </div>
        </div>
        {canRegenerate && (
          <button
            onClick={() => fetchBlueprint(true)}
            disabled={regenerating}
            data-testid="ai-blueprint-regenerate"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-md border border-slate-700 text-slate-300 hover:text-emerald-400 hover:border-emerald-500/40 transition disabled:opacity-50"
          >
            {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        )}
      </div>

      {/* Focus Priority */}
      <div className="border-l-2 border-emerald-500 pl-4 py-1 mb-4 relative z-10" data-testid="ai-blueprint-focus">
        <span className="text-[10px] text-slate-500 font-extrabold uppercase tracking-wider block mb-0.5">
          Primary Development Priority
        </span>
        <h5 className="text-base sm:text-lg font-black text-white tracking-tight leading-tight">
          {bp.focus_title}
        </h5>
        {ctx.metric_analyzed && (
          <p className="text-[11px] text-slate-400 mt-1">
            <span className="capitalize">{String(ctx.metric_analyzed).replace(/_/g, ' ')}</span>
            <span className="mx-1.5 opacity-40">·</span>
            scored <span className="font-bold text-amber-400">{ctx.score}/5</span>
          </p>
        )}
      </div>

      {/* Drill + Conditioning grid */}
      <div className={`grid gap-3 mb-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        <div
          className="bg-slate-900/40 border border-slate-900 hover:border-slate-800 p-4 rounded-xl space-y-2 transition relative z-10"
          data-testid="ai-blueprint-home-drill"
        >
          <span className="text-[10px] text-emerald-400 font-extrabold uppercase tracking-widest flex items-center gap-1">
            <Flame className="w-3 h-3" /> Home Training Drill
          </span>
          <p className="text-xs text-slate-300 font-medium leading-relaxed">
            {bp.home_drill}
          </p>
        </div>

        <div
          className="bg-slate-900/40 border border-slate-900 hover:border-slate-800 p-4 rounded-xl space-y-2 transition relative z-10"
          data-testid="ai-blueprint-conditioning"
        >
          <span className="text-[10px] text-indigo-400 font-extrabold uppercase tracking-widest flex items-center gap-1">
            <Dumbbell className="w-3 h-3" /> Athletic Conditioning Focus
          </span>
          <p className="text-xs text-slate-300 font-medium leading-relaxed">
            {bp.athletic_target}
          </p>
        </div>
      </div>

      {/* Phase 197.c — Drill Library video embed */}
      {bp.video_match?.has_video && (
        <div
          className="mb-4 border border-slate-900 rounded-xl overflow-hidden bg-slate-900/30 relative z-10"
          data-testid="ai-blueprint-video"
        >
          <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2">
            <span className="text-[10px] text-amber-400 font-extrabold uppercase tracking-widest flex items-center gap-1.5">
              <Video className="w-3 h-3" /> Recommended Instructional Video
            </span>
            <span className="text-[10px] text-slate-500 truncate max-w-[60%]">
              {bp.video_match.title}
            </span>
          </div>
          {bp.video_match.youtube_id ? (
            <div className="aspect-video bg-slate-950">
              <iframe
                src={`https://www.youtube.com/embed/${bp.video_match.youtube_id}`}
                className="w-full h-full border-0"
                allowFullScreen
                title={bp.video_match.title || 'Drill video'}
                data-testid="ai-blueprint-video-iframe"
              />
            </div>
          ) : (
            <a
              href={bp.video_match.video_url}
              target="_blank"
              rel="noreferrer"
              className="block px-4 pb-3 text-xs text-amber-300 hover:text-amber-200 underline truncate"
              data-testid="ai-blueprint-video-link"
            >
              Open drill video →
            </a>
          )}
        </div>
      )}

      {/* Reassessment + Parent note footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-800/60 relative z-10">
        <div
          className="flex items-center gap-2 text-[11px] text-slate-400"
          data-testid="ai-blueprint-reassessment"
        >
          <Calendar className="w-3.5 h-3.5 text-amber-400" />
          <span className="font-bold text-slate-200">{bp.next_reassessment_label || `Re-test in ${bp.reassessment_days || 21} days`}</span>
        </div>
        {bp.parent_note && (
          <div
            className="flex items-center gap-1.5 text-[11px] italic text-slate-400 max-w-md"
            data-testid="ai-blueprint-parent-note"
          >
            <Heart className="w-3 h-3 text-rose-400 shrink-0" />
            <span>{bp.parent_note}</span>
          </div>
        )}
      </div>
    </div>
  );
}
